import React from "react";
import { useTranslation } from "react-i18next";
import U2000ActionBtn from "../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000ResponsiveToolbar from "../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";
import { FaCalendarPlus, FaPlus, FaRegFile, FaTrashAlt } from "react-icons/fa";

import { useU2000AuthContext } from "../../../u2000Core/context/auth/U2000UseAuthContext";
import type { U2000GridStateHandlers } from "../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { U2000TableHandlers } from "../../../u2000Core/types/U2000TableHandlers";
import { NS, U3101ROLES } from "../../U3101";

interface U3101EmployeeListBtnsCompomentProps<T extends Record<string, any>> {
  gridStateHandlers: U2000GridStateHandlers<T>;
  handlerActions?: U2000TableHandlers<T>;
  onPrepareHourBanksNextYear?: () => void;
}

export const U3101EmployeeListToolBar: React.FC<U3101EmployeeListBtnsCompomentProps<Record<string, any>>> = ({
  gridStateHandlers,
  handlerActions,
  onPrepareHourBanksNextYear,
}) => {
  const { t } = useTranslation();
  const { hasRole } = useU2000AuthContext();

  const disableUpdateBtn = React.useMemo(() => {
    const selectedRows = gridStateHandlers.getSelectedRows();
    return selectedRows.length !== 1;
  }, [gridStateHandlers.getSelectedRows()]);

  const disableDeleteBtn = React.useMemo(() => {
    const selectedRows = gridStateHandlers.getSelectedRows();
    return selectedRows.length === 0;
  }, [gridStateHandlers.getSelectedRows()]);

  const actions = [
    {
      key: "add",
      label: t("btnAdd", { ns: NS }),
      hasRole: hasRole(U3101ROLES.ADD),
      handleClick: handlerActions?.onAdd,
      icon: FaPlus,
      Component: U2000ActionBtn,
    },
    {
      key: "modify",
      label: t("btnModify", { ns: NS }),
      hasRole: hasRole(U3101ROLES.EDIT),
      handleClick: handlerActions?.onModify,
      disabled: disableUpdateBtn,
      icon: FaRegFile,
      Component: U2000ActionBtn,
    },
    {
      key: "delete",
      label: t("btnDelete", { ns: NS }),
      hasRole: hasRole(U3101ROLES.DELETE),
      handleClick: handlerActions?.onDelete,
      disabled: disableDeleteBtn,
      icon: FaTrashAlt,
      Component: U2000ActionBtn,
    },
    {
      key: "prepareHourBanksNextYear",
      label: t("btnPrepareHourBanksBulk", { ns: NS }),
      hasRole: hasRole(U3101ROLES.EDIT),
      handleClick: onPrepareHourBanksNextYear,
      icon: FaCalendarPlus,
      Component: U2000ActionBtn,
    },
  ];

  return <U2000ResponsiveToolbar actions={actions} />;
};
